import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '@/lib/api';
import { ArrowLeft, Plus, X, GripVertical, Trash2 } from 'lucide-react';
import AIOptimizePanel from '@/components/admin/AIOptimizePanel';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';

const emptyVariant = () => ({
  id: `v-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  title: '', option1: '', option2: '', option3: '',
  price: 0, compare_at_price: 0, sku: '', barcode: '', stock_quantity: 0, weight: 0, weight_unit: 'kg',
});

export default function ProductFormPage() {
  const { handle } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(handle);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);

  const [form, setForm] = useState({
    title: '', handle: '', description: '', price: 0, compare_at_price: 0, cost_price: 0,
    sku: '', barcode_gtin: '', brand: '', vendor: '', product_type: '', category: '',
    tags: [], images: [], variants: [], options: [],
    seo_title: '', seo_description: '', status: 'draft',
    stock_quantity: 0, low_stock_threshold: 5, weight: 0, weight_unit: 'kg',
    google_product_category: '', condition: 'new',
  });
  const [tagInput, setTagInput] = useState('');
  const [imageUrl, setImageUrl] = useState('');

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/products/${handle}`).then(r => {
      setForm(r.data);
      setLoading(false);
    }).catch(() => {
      toast.error('Product not found');
      navigate('/admin/products');
    });
  }, [handle, isEdit, navigate]);

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const addTag = () => {
    const tag = tagInput.trim();
    if (tag && !form.tags.includes(tag)) {
      set('tags', [...form.tags, tag]);
    }
    setTagInput('');
  };

  const removeTag = (tag) => set('tags', form.tags.filter(t => t !== tag));

  const addImage = () => {
    if (!imageUrl.trim()) return;
    set('images', [...form.images, { url: imageUrl.trim(), alt_text: '', position: form.images.length + 1 }]);
    setImageUrl('');
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      set('images', [...form.images, { url: data.url, alt_text: file.name, position: form.images.length + 1 }]);
      toast.success('Image uploaded');
    } catch {
      toast.error('Upload failed');
    }
  };

  const removeImage = (idx) => set('images', form.images.filter((_, i) => i !== idx));

  const moveImage = (idx, dir) => {
    const imgs = [...form.images];
    const swap = idx + dir;
    if (swap < 0 || swap >= imgs.length) return;
    [imgs[idx], imgs[swap]] = [imgs[swap], imgs[idx]];
    imgs.forEach((img, i) => { img.position = i + 1; });
    set('images', imgs);
  };

  const addVariant = () => set('variants', [...form.variants, emptyVariant()]);
  const updateVariant = (idx, key, val) => {
    const vars = [...form.variants];
    vars[idx] = { ...vars[idx], [key]: val };
    if (['option1', 'option2', 'option3'].includes(key)) {
      vars[idx].title = [vars[idx].option1, vars[idx].option2, vars[idx].option3].filter(Boolean).join(' / ');
    }
    set('variants', vars);
  };
  const removeVariant = (idx) => set('variants', form.variants.filter((_, i) => i !== idx));

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, price: parseFloat(form.price) || 0, compare_at_price: parseFloat(form.compare_at_price) || 0, cost_price: parseFloat(form.cost_price) || 0, stock_quantity: parseInt(form.stock_quantity) || 0, weight: parseFloat(form.weight) || 0 };
      if (isEdit) {
        await api.put(`/products/${handle}`, payload);
        toast.success('Product updated');
      } else {
        await api.post('/products', payload);
        toast.success('Product created');
      }
      navigate('/admin/products');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  if (loading) return <div className="p-12 text-center font-sans text-sm" style={{ color: 'rgba(26,26,26,0.4)' }}>Loading...</div>;

  const inputStyle = { borderColor: 'rgba(26,26,26,0.12)' };
  const labelStyle = { color: 'rgba(26,26,26,0.6)' };

  return (
    <motion.div data-testid="product-form-page" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <button data-testid="back-to-products" onClick={() => navigate('/admin/products')}
          className="p-2 rounded-lg hover:bg-[rgba(26,26,26,0.05)] transition-colors">
          <ArrowLeft size={18} strokeWidth={1.5} />
        </button>
        <div className="flex-1">
          <h1 className="font-serif text-2xl tracking-tight" style={{ color: '#1A1A1A' }}>
            {isEdit ? 'Edit Product' : 'New Product'}
          </h1>
        </div>
        <Select value={form.status} onValueChange={v => set('status', v)}>
          <SelectTrigger data-testid="status-select" className="w-[120px] font-sans text-sm rounded-none" style={inputStyle}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>
        <Button data-testid="save-product" onClick={handleSave} disabled={saving}
          className="font-sans text-sm rounded-none px-6" style={{ backgroundColor: '#E85D3A', color: '#fff' }}>
          {saving ? 'Saving...' : 'Save Product'}
        </Button>
      </div>

      <Tabs defaultValue="general" className="space-y-6">
        <TabsList className="bg-transparent border-b rounded-none w-full justify-start p-0 h-auto" style={{ borderColor: 'rgba(26,26,26,0.08)' }}>
          {['general', 'variants', 'images', 'seo'].map(t => (
            <TabsTrigger key={t} value={t} data-testid={`tab-${t}`}
              className="font-sans text-sm rounded-none border-b-2 border-transparent data-[state=active]:border-[#C4956A] data-[state=active]:text-[#1A1A1A] px-4 pb-3 pt-1 capitalize"
              style={{ color: 'rgba(26,26,26,0.5)' }}>
              {t}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* General Tab */}
        <TabsContent value="general">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <div className="p-6 rounded-lg border" style={{ backgroundColor: '#FDFBF7', borderColor: 'rgba(26,26,26,0.08)' }}>
                <div className="space-y-4">
                  <div>
                    <Label className="font-sans text-xs uppercase tracking-wider mb-1.5 block" style={labelStyle}>Title</Label>
                    <Input data-testid="product-title" value={form.title} onChange={e => set('title', e.target.value)}
                      className="font-sans rounded-none" style={inputStyle} placeholder="e.g. Essential Face Serum" />
                  </div>
                  <div>
                    <Label className="font-sans text-xs uppercase tracking-wider mb-1.5 block" style={labelStyle}>Handle (URL slug)</Label>
                    <Input data-testid="product-handle" value={form.handle} onChange={e => set('handle', e.target.value)}
                      className="font-sans rounded-none" style={inputStyle} placeholder="auto-generated from title" />
                  </div>
                  <div>
                    <Label className="font-sans text-xs uppercase tracking-wider mb-1.5 block" style={labelStyle}>Description (HTML)</Label>
                    <Textarea data-testid="product-description" value={form.description} onChange={e => set('description', e.target.value)}
                      className="font-sans rounded-none min-h-[160px]" style={inputStyle} placeholder="<p>Product description with HTML...</p>" />
                  </div>
                </div>
              </div>

              {/* Pricing */}
              <div className="p-6 rounded-lg border" style={{ backgroundColor: '#FDFBF7', borderColor: 'rgba(26,26,26,0.08)' }}>
                <h3 className="font-sans text-xs uppercase tracking-wider font-medium mb-4" style={{ color: '#C4956A' }}>Pricing</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="font-sans text-xs mb-1.5 block" style={labelStyle}>Price</Label>
                    <Input data-testid="product-price" type="number" step="0.01" value={form.price} onChange={e => set('price', e.target.value)}
                      className="font-sans rounded-none" style={inputStyle} />
                  </div>
                  <div>
                    <Label className="font-sans text-xs mb-1.5 block" style={labelStyle}>Compare at Price</Label>
                    <Input data-testid="product-compare-price" type="number" step="0.01" value={form.compare_at_price} onChange={e => set('compare_at_price', e.target.value)}
                      className="font-sans rounded-none" style={inputStyle} />
                  </div>
                  <div>
                    <Label className="font-sans text-xs mb-1.5 block" style={labelStyle}>Cost Price</Label>
                    <Input type="number" step="0.01" value={form.cost_price} onChange={e => set('cost_price', e.target.value)}
                      className="font-sans rounded-none" style={inputStyle} />
                  </div>
                </div>
              </div>

              {/* Inventory */}
              <div className="p-6 rounded-lg border" style={{ backgroundColor: '#FDFBF7', borderColor: 'rgba(26,26,26,0.08)' }}>
                <h3 className="font-sans text-xs uppercase tracking-wider font-medium mb-4" style={{ color: '#C4956A' }}>Inventory & Shipping</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <Label className="font-sans text-xs mb-1.5 block" style={labelStyle}>SKU</Label>
                    <Input data-testid="product-sku" value={form.sku} onChange={e => set('sku', e.target.value)}
                      className="font-sans rounded-none" style={inputStyle} />
                  </div>
                  <div>
                    <Label className="font-sans text-xs mb-1.5 block" style={labelStyle}>Barcode (GTIN)</Label>
                    <Input value={form.barcode_gtin} onChange={e => set('barcode_gtin', e.target.value)}
                      className="font-sans rounded-none" style={inputStyle} />
                  </div>
                  <div>
                    <Label className="font-sans text-xs mb-1.5 block" style={labelStyle}>Stock Qty</Label>
                    <Input data-testid="product-stock" type="number" value={form.stock_quantity} onChange={e => set('stock_quantity', e.target.value)}
                      className="font-sans rounded-none" style={inputStyle} />
                  </div>
                  <div>
                    <Label className="font-sans text-xs mb-1.5 block" style={labelStyle}>Low Stock Alert</Label>
                    <Input type="number" value={form.low_stock_threshold} onChange={e => set('low_stock_threshold', e.target.value)}
                      className="font-sans rounded-none" style={inputStyle} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div>
                    <Label className="font-sans text-xs mb-1.5 block" style={labelStyle}>Weight</Label>
                    <Input type="number" step="0.01" value={form.weight} onChange={e => set('weight', e.target.value)}
                      className="font-sans rounded-none" style={inputStyle} />
                  </div>
                  <div>
                    <Label className="font-sans text-xs mb-1.5 block" style={labelStyle}>Weight Unit</Label>
                    <Select value={form.weight_unit} onValueChange={v => set('weight_unit', v)}>
                      <SelectTrigger className="font-sans text-sm rounded-none" style={inputStyle}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="kg">kg</SelectItem>
                        <SelectItem value="g">g</SelectItem>
                        <SelectItem value="lb">lb</SelectItem>
                        <SelectItem value="oz">oz</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>

            {/* Right sidebar */}
            <div className="space-y-6">
              <div className="p-6 rounded-lg border" style={{ backgroundColor: '#FDFBF7', borderColor: 'rgba(26,26,26,0.08)' }}>
                <h3 className="font-sans text-xs uppercase tracking-wider font-medium mb-4" style={{ color: '#C4956A' }}>Organization</h3>
                <div className="space-y-4">
                  <div>
                    <Label className="font-sans text-xs mb-1.5 block" style={labelStyle}>Category</Label>
                    <Input data-testid="product-category" value={form.category} onChange={e => set('category', e.target.value)}
                      className="font-sans rounded-none" style={inputStyle} placeholder="e.g. Skincare" />
                  </div>
                  <div>
                    <Label className="font-sans text-xs mb-1.5 block" style={labelStyle}>Product Type</Label>
                    <Input value={form.product_type} onChange={e => set('product_type', e.target.value)}
                      className="font-sans rounded-none" style={inputStyle} />
                  </div>
                  <div>
                    <Label className="font-sans text-xs mb-1.5 block" style={labelStyle}>Brand</Label>
                    <Input value={form.brand} onChange={e => set('brand', e.target.value)}
                      className="font-sans rounded-none" style={inputStyle} />
                  </div>
                  <div>
                    <Label className="font-sans text-xs mb-1.5 block" style={labelStyle}>Vendor</Label>
                    <Input value={form.vendor} onChange={e => set('vendor', e.target.value)}
                      className="font-sans rounded-none" style={inputStyle} />
                  </div>
                  <div>
                    <Label className="font-sans text-xs mb-1.5 block" style={labelStyle}>Tags</Label>
                    <div className="flex gap-2">
                      <Input data-testid="tag-input" value={tagInput} onChange={e => setTagInput(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addTag())}
                        className="font-sans rounded-none flex-1" style={inputStyle} placeholder="Add tag" />
                      <Button type="button" variant="outline" onClick={addTag} className="rounded-none" style={inputStyle}>
                        <Plus size={14} />
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {form.tags.map(tag => (
                        <span key={tag} className="inline-flex items-center gap-1 px-2 py-0.5 font-sans text-xs rounded-sm"
                          style={{ backgroundColor: 'rgba(196,149,106,0.1)', color: '#C4956A' }}>
                          {tag}
                          <button onClick={() => removeTag(tag)}><X size={10} /></button>
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-6 rounded-lg border" style={{ backgroundColor: '#FDFBF7', borderColor: 'rgba(26,26,26,0.08)' }}>
                <h3 className="font-sans text-xs uppercase tracking-wider font-medium mb-4" style={{ color: '#C4956A' }}>Google Shopping</h3>
                <div className="space-y-4">
                  <div>
                    <Label className="font-sans text-xs mb-1.5 block" style={labelStyle}>Product Category</Label>
                    <Input value={form.google_product_category} onChange={e => set('google_product_category', e.target.value)}
                      className="font-sans rounded-none text-xs" style={inputStyle} placeholder="Health & Beauty > ..." />
                  </div>
                  <div>
                    <Label className="font-sans text-xs mb-1.5 block" style={labelStyle}>Condition</Label>
                    <Select value={form.condition} onValueChange={v => set('condition', v)}>
                      <SelectTrigger className="font-sans text-sm rounded-none" style={inputStyle}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new">New</SelectItem>
                        <SelectItem value="refurbished">Refurbished</SelectItem>
                        <SelectItem value="used">Used</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* AI Optimize Panel */}
              {isEdit && (
                <AIOptimizePanel product={form} onApply={(updates) => {
                  setForm(prev => ({ ...prev, ...updates }));
                }} />
              )}
            </div>
          </div>
        </TabsContent>
        <TabsContent value="variants">
          <div className="p-6 rounded-lg border" style={{ backgroundColor: '#FDFBF7', borderColor: 'rgba(26,26,26,0.08)' }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-sans text-xs uppercase tracking-wider font-medium" style={{ color: '#C4956A' }}>Variants</h3>
              <Button data-testid="add-variant" onClick={addVariant} variant="outline" size="sm" className="font-sans text-xs rounded-none gap-1" style={inputStyle}>
                <Plus size={13} /> Add Variant
              </Button>
            </div>
            {form.variants.length === 0 ? (
              <p className="font-sans text-sm py-8 text-center" style={{ color: 'rgba(26,26,26,0.4)' }}>No variants. Add variants for products with multiple options (size, color, etc.)</p>
            ) : (
              <div className="space-y-3">
                {form.variants.map((v, idx) => (
                  <div key={v.id} className="p-4 rounded border" style={{ borderColor: 'rgba(26,26,26,0.08)' }}>
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-sans text-sm font-medium" style={{ color: '#1A1A1A' }}>Variant {idx + 1}</span>
                      <button data-testid={`remove-variant-${idx}`} onClick={() => removeVariant(idx)} className="p-1 rounded hover:bg-red-50 transition-colors">
                        <Trash2 size={14} strokeWidth={1.5} className="text-red-500" />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div>
                        <Label className="font-sans text-[10px] uppercase tracking-wider mb-1 block" style={labelStyle}>Option 1</Label>
                        <Input value={v.option1} onChange={e => updateVariant(idx, 'option1', e.target.value)}
                          className="font-sans text-sm rounded-none" style={inputStyle} placeholder="e.g. Small" />
                      </div>
                      <div>
                        <Label className="font-sans text-[10px] uppercase tracking-wider mb-1 block" style={labelStyle}>Option 2</Label>
                        <Input value={v.option2} onChange={e => updateVariant(idx, 'option2', e.target.value)}
                          className="font-sans text-sm rounded-none" style={inputStyle} placeholder="e.g. Red" />
                      </div>
                      <div>
                        <Label className="font-sans text-[10px] uppercase tracking-wider mb-1 block" style={labelStyle}>Price</Label>
                        <Input type="number" step="0.01" value={v.price} onChange={e => updateVariant(idx, 'price', parseFloat(e.target.value) || 0)}
                          className="font-sans text-sm rounded-none" style={inputStyle} />
                      </div>
                      <div>
                        <Label className="font-sans text-[10px] uppercase tracking-wider mb-1 block" style={labelStyle}>Stock</Label>
                        <Input type="number" value={v.stock_quantity} onChange={e => updateVariant(idx, 'stock_quantity', parseInt(e.target.value) || 0)}
                          className="font-sans text-sm rounded-none" style={inputStyle} />
                      </div>
                      <div>
                        <Label className="font-sans text-[10px] uppercase tracking-wider mb-1 block" style={labelStyle}>SKU</Label>
                        <Input value={v.sku} onChange={e => updateVariant(idx, 'sku', e.target.value)}
                          className="font-sans text-sm rounded-none" style={inputStyle} />
                      </div>
                      <div>
                        <Label className="font-sans text-[10px] uppercase tracking-wider mb-1 block" style={labelStyle}>Compare Price</Label>
                        <Input type="number" step="0.01" value={v.compare_at_price} onChange={e => updateVariant(idx, 'compare_at_price', parseFloat(e.target.value) || 0)}
                          className="font-sans text-sm rounded-none" style={inputStyle} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Images Tab */}
        <TabsContent value="images">
          <div className="p-6 rounded-lg border" style={{ backgroundColor: '#FDFBF7', borderColor: 'rgba(26,26,26,0.08)' }}>
            <h3 className="font-sans text-xs uppercase tracking-wider font-medium mb-4" style={{ color: '#C4956A' }}>Product Images</h3>
            <div className="flex gap-3 mb-4">
              <Input data-testid="image-url-input" value={imageUrl} onChange={e => setImageUrl(e.target.value)}
                placeholder="Paste image URL" className="font-sans rounded-none flex-1" style={inputStyle} />
              <Button data-testid="add-image-url" onClick={addImage} variant="outline" className="rounded-none font-sans text-sm" style={inputStyle}>Add URL</Button>
              <div className="relative">
                <input type="file" accept="image/*" onChange={uploadImage} className="absolute inset-0 opacity-0 cursor-pointer" data-testid="upload-image-input" />
                <Button variant="outline" className="rounded-none font-sans text-sm" style={inputStyle}>Upload</Button>
              </div>
            </div>
            {form.images.length === 0 ? (
              <p className="font-sans text-sm py-8 text-center" style={{ color: 'rgba(26,26,26,0.4)' }}>No images added yet.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {form.images.map((img, idx) => (
                  <div key={idx} className="relative group rounded overflow-hidden border" style={{ borderColor: 'rgba(26,26,26,0.08)' }}>
                    <div className="aspect-square bg-[rgba(26,26,26,0.03)]">
                      <img src={img.url} alt={img.alt_text} className="w-full h-full object-cover" />
                    </div>
                    <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {idx > 0 && (
                        <button onClick={() => moveImage(idx, -1)} className="p-1 rounded bg-white/90 shadow-sm hover:bg-white" title="Move left">
                          <GripVertical size={12} />
                        </button>
                      )}
                      <button data-testid={`remove-image-${idx}`} onClick={() => removeImage(idx)} className="p-1 rounded bg-white/90 shadow-sm hover:bg-red-50">
                        <X size={12} className="text-red-500" />
                      </button>
                    </div>
                    {idx === 0 && (
                      <span className="absolute bottom-1 left-1 px-1.5 py-0.5 font-sans text-[9px] uppercase tracking-wider rounded"
                        style={{ backgroundColor: '#C4956A', color: '#fff' }}>Main</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* SEO Tab */}
        <TabsContent value="seo">
          <div className="p-6 rounded-lg border max-w-2xl" style={{ backgroundColor: '#FDFBF7', borderColor: 'rgba(26,26,26,0.08)' }}>
            <h3 className="font-sans text-xs uppercase tracking-wider font-medium mb-4" style={{ color: '#C4956A' }}>SEO & Meta</h3>
            <div className="space-y-4">
              <div>
                <Label className="font-sans text-xs mb-1.5 block" style={labelStyle}>SEO Title</Label>
                <Input data-testid="seo-title" value={form.seo_title} onChange={e => set('seo_title', e.target.value)}
                  className="font-sans rounded-none" style={inputStyle} placeholder={form.title || 'Product title'} />
                <p className="font-sans text-[10px] mt-1" style={{ color: 'rgba(26,26,26,0.35)' }}>{(form.seo_title || form.title || '').length}/70 characters</p>
              </div>
              <div>
                <Label className="font-sans text-xs mb-1.5 block" style={labelStyle}>SEO Description</Label>
                <Textarea data-testid="seo-description" value={form.seo_description} onChange={e => set('seo_description', e.target.value)}
                  className="font-sans rounded-none min-h-[80px]" style={inputStyle} placeholder="Meta description for search engines" />
                <p className="font-sans text-[10px] mt-1" style={{ color: 'rgba(26,26,26,0.35)' }}>{(form.seo_description || '').length}/160 characters</p>
              </div>

              {/* Preview */}
              <div className="mt-6 p-4 rounded border" style={{ borderColor: 'rgba(26,26,26,0.06)', backgroundColor: '#fff' }}>
                <p className="font-sans text-xs mb-2" style={{ color: 'rgba(26,26,26,0.35)' }}>Google Preview</p>
                <p className="font-sans text-base" style={{ color: '#1a0dab' }}>{form.seo_title || form.title || 'Product Title'}</p>
                <p className="font-sans text-xs" style={{ color: '#006621' }}>store.com/product/{form.handle || 'product-handle'}</p>
                <p className="font-sans text-sm mt-0.5" style={{ color: '#545454' }}>{form.seo_description || 'Add a meta description...'}</p>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </motion.div>
  );
}
